// src/contexts/InsightFlareContext.tsx - CORRECTED VERSION
import React, { createContext, useContext, useEffect, ReactNode, useState, useCallback } from 'react';

interface InsightFlareContextType {
  identify: (userId: string, userName?: string) => void;
  track: (event: string, properties?: Record<string, any>) => void;
  reset: () => void;
  setGlobalProperties: (properties: Record<string, any>) => void;
  clearGlobalProperties: () => void;
  trackOnce: (event: string, properties?: Record<string, any>) => void;
  isReady: boolean;
}

const InsightFlareContext = createContext<InsightFlareContextType>({
  identify: () => {},
  track: () => {},
  reset: () => {},
  setGlobalProperties: () => {},
  clearGlobalProperties: () => {},
  trackOnce: () => {},
  isReady: false,
});

export const useInsightFlare = () => useContext(InsightFlareContext);

interface InsightFlareProviderProps {
  children: ReactNode;
  enabled?: boolean;
}

export const InsightFlareProvider: React.FC<InsightFlareProviderProps> = ({
  children,
  enabled = true,
}) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    // Check if InsightFlare is already loaded
    const checkLoaded = () => {
      if (window.insightflare && typeof window.insightflare === 'object') {
        setIsReady(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkLoaded()) {
      return;
    }

    // Set up polling to check when loaded
    const interval = setInterval(() => {
      if (checkLoaded()) {
        clearInterval(interval);
        console.log('InsightFlare loaded');
      }
    }, 100);

    // Cleanup
    return () => clearInterval(interval);
  }, [enabled]);

  const identify = useCallback((userId: string, userName?: string) => {
    if (!enabled || typeof window === 'undefined' || !window.insightflare) {
      console.warn('InsightFlare not ready for identify');
      return;
    }
    
    try {
      // CORRECT API: identify(userId, userName)
      window.insightflare.identify(userId, { name: userName });
      console.log('InsightFlare identify called:', userId, userName);
    } catch (error) {
      console.error('InsightFlare identify error:', error);
    }
  }, [enabled]);

  const track = useCallback((event: string, properties?: Record<string, any>) => {
    if (!enabled || typeof window === 'undefined' || !window.insightflare) {
      console.warn('InsightFlare not ready for track');
      return;
    }
    
    try {
      window.insightflare.track(event, properties);
      console.log('InsightFlare track called:', event, properties);
    } catch (error) {
      console.error('InsightFlare track error:', error);
    }
  }, [enabled]);

  const reset = useCallback(() => {
    if (!enabled || typeof window === 'undefined' || !window.insightflare) {
      console.warn('InsightFlare not ready for reset');
      return;
    }
    
    try {
      window.insightflare.reset();
      console.log('InsightFlare reset called');
    } catch (error) {
      console.error('InsightFlare reset error:', error);
    }
  }, [enabled]);

  const setGlobalProperties = useCallback((properties: Record<string, any>) => {
    if (!enabled || typeof window === 'undefined' || !window.insightflare) {
      console.warn('InsightFlare not ready for setGlobalProperties');
      return;
    }
    
    try {
      window.insightflare.setGlobalProperties(properties);
    } catch (error) {
      console.error('InsightFlare setGlobalProperties error:', error);
    }
  }, [enabled]);

  const clearGlobalProperties = useCallback(() => {
    if (!enabled || typeof window === 'undefined' || !window.insightflare) {
      console.warn('InsightFlare not ready for clearGlobalProperties');
      return;
    }
    
    try {
      window.insightflare.clearGlobalProperties();
    } catch (error) {
      console.error('InsightFlare clearGlobalProperties error:', error);
    }
  }, [enabled]);

  const trackOnce = useCallback((event: string, properties?: Record<string, any>) => {
    if (!enabled || typeof window === 'undefined' || !window.insightflare) {
      console.warn('InsightFlare not ready for trackOnce');
      return;
    }
    
    try {
      window.insightflare.trackOnce(event, properties);
    } catch (error) {
      console.error('InsightFlare trackOnce error:', error);
    }
  }, [enabled]);

  // Debug logging
  useEffect(() => {
    if (enabled && isReady) {
      console.log('InsightFlare Provider: Ready to use');
    }
  }, [enabled, isReady]);

  const value = {
    identify,
    track,
    reset,
    setGlobalProperties,
    clearGlobalProperties,
    trackOnce,
    isReady,
  };

  return (
    <InsightFlareContext.Provider value={value}>
      {children}
    </InsightFlareContext.Provider>
  );
};

// Type declaration for window
declare global {
  interface Window {
    insightflare?: {
      track: (event: string, properties?: Record<string, any>) => void;
      identify: (userId: string, traits?: { name?: string }) => void;
      reset: () => void;
      setGlobalProperties: (properties: Record<string, any>) => void;
      clearGlobalProperties: () => void;
      trackOnce: (event: string, properties?: Record<string, any>) => void;
      debug: () => void;
    };
  }
}